import { useCallback, useState } from 'react';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;

function clamp(z: number) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

interface CanvasState {
  zoom: number;
  panX: number;
  panY: number;
}

export function useWorkspaceCanvas() {
  const [state, setState] = useState<CanvasState>({ zoom: 1, panX: 0, panY: 0 });

  const zoomAt = useCallback((factor: number, mx: number, my: number) => {
    setState((prev) => {
      const newZoom = clamp(prev.zoom * factor);
      const ratio = newZoom / prev.zoom;
      return {
        zoom: newZoom,
        panX: mx * (1 - ratio) + prev.panX * ratio,
        panY: my * (1 - ratio) + prev.panY * ratio,
      };
    });
  }, []);

  const setZoom = useCallback((newZoom: number, mx: number, my: number) => {
    setState((prev) => {
      const clamped = clamp(newZoom);
      const ratio = clamped / prev.zoom;
      return {
        zoom: clamped,
        panX: mx * (1 - ratio) + prev.panX * ratio,
        panY: my * (1 - ratio) + prev.panY * ratio,
      };
    });
  }, []);

  const setPan = useCallback((panX: number, panY: number) => {
    setState((prev) => ({ ...prev, panX, panY }));
  }, []);

  const fitToContent = useCallback(
    (containerW: number, containerH: number, contentW: number, contentH: number) => {
      if (contentW === 0 || contentH === 0) return;
      const padding = 40;
      const newZoom = clamp(
        Math.min((containerW - padding * 2) / contentW, (containerH - padding * 2) / contentH),
      );
      setState({
        zoom: newZoom,
        panX: (containerW - contentW * newZoom) / 2,
        panY: (containerH - contentH * newZoom) / 2,
      });
    },
    [],
  );

  return { ...state, zoomAt, setZoom, setPan, fitToContent };
}
